import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { createEmployee, type CreateEmployeeInput } from '@/lib/employees';
import { employeesKeys } from '@/hooks/use-employees';
import { extractErrorMessage } from '@/lib/errors';
import { EmployeeForm, type EmployeeFormDefaults } from './employee-form';

interface AddEmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (employeeId: string) => void;
  defaults?: EmployeeFormDefaults;
  title?: string;
  description?: string;
}

export function AddEmployeeDialog({
  open,
  onOpenChange,
  onSuccess,
  defaults,
  title,
  description,
}: AddEmployeeDialogProps) {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  // Bump on close to force-remount EmployeeForm (clears refs/uncontrolled inputs).
  const [formKey, setFormKey] = useState(0);

  const createMutation = useMutation({
    mutationFn: (input: CreateEmployeeInput) => createEmployee(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeesKeys.stats() });
      onOpenChange(false);
      onSuccess?.(data.id);
    },
    onError: (error: unknown) => {
      setServerError(extractErrorMessage(error, 'Failed to create employee. Please try again.'));
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setServerError(null);
      setFormKey((k) => k + 1);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg">{title ?? 'Add Employee'}</DialogTitle>
          <DialogDescription className="text-sm">
            {description ?? 'Create a new employee profile.'}
          </DialogDescription>
        </DialogHeader>

        <EmployeeForm
          key={formKey}
          defaults={defaults}
          onSubmit={(input) => {
            setServerError(null);
            createMutation.mutate(input);
          }}
          onCancel={() => handleOpenChange(false)}
          isSubmitting={createMutation.isPending}
          serverError={serverError}
        />
      </DialogContent>
    </Dialog>
  );
}
